/**
 * Typed fetch wrapper for the HEROs API.
 *
 * - Same-origin: hits `/api/*` (Vite proxies to the API on :3000 in dev).
 * - `credentials: 'include'` so the session cookie rides along.
 * - CSRF: the server's `_csrf` cookie is httpOnly (JS cannot read it), so we
 *   fetch a token from `GET /api/csrf` once, cache it IN MEMORY, and echo it on
 *   every mutating request (POST/PUT/PATCH/DELETE) via the `X-CSRF-Token`
 *   header. The token is (re)fetched on bootstrap and after login/logout. If a
 *   mutating request 403s for a missing/stale token we refresh once and retry.
 * - Centralised error handling: every non-2xx response is turned into an
 *   {@link ApiClientError} carrying the HTTP status + parsed `ApiError` body so
 *   callers can branch on 401/403/429/400.
 *
 * Returned types come straight from `@heros/shared`.
 */
import type {
  User,
  Project,
  TimelineItem,
  TaskStatus,
  Quotation,
  QuotationStatus,
  QuotationSection,
  QuotationAdjustments,
  CatalogItem,
  DataManagementItem,
  PricingLibrary,
  PricingProtocolRow,
  PricingDescriptiveRow,
  PricingTopupRow,
  Compensation,
  CompensationGroup,
  CompensationSplit,
  SplitState,
  ActivityLog,
  DocumentMeta,
  DocumentStatus,
  Meeting,
  PendingUser,
  ApiError,
  MeResponse,
  LoginRequest,
  Role,
  HealthResponse,
  MyProfileResponse,
  UserProfile,
  UserCvEntry,
  UserCertificateEntry,
  PublicProfileResponse,
  PublicUserSummary,
  ForgotPasswordResponse,
  ResetPasswordResponse,
  ChangePasswordRequest,
  PasswordResetRequest,
  PasswordResetRequestStatus,
  AppNotification,
  ProjectTemplate,
  AnalysisFormula,
} from '@heros/shared';

/* ------------------------------------------------------------------ *
 * Resource-specific request/response shapes (beyond the bare DTOs).
 * ------------------------------------------------------------------ */

/** The loosely-typed builder form the quotation engine consumes server-side. */
export type BuilderForm = Record<string, unknown>;

/** Stateless preview returned by POST /api/quotations/compute. */
export interface QuotationComputeResult {
  sections: QuotationSection[];
  grandTotal: number;
  meta: Record<string, unknown>;
}

/** A single split on a compensation, augmented with its server id + derived state. */
export interface CompensationSplitView extends CompensationSplit {
  /** Server split id (needed for the per-split "received" toggle). */
  id?: string;
  state?: SplitState;
}

/**
 * Compensation as returned by the list/single endpoints: the base DTO plus the
 * derived `taskCompleted` flag and a `splitStates` map keyed by split id.
 */
export interface CompensationView extends Compensation {
  taskCompleted?: boolean;
  /** splitId → derived lifecycle state. */
  splitStates?: Record<string, SplitState>;
}

/** Per-split outstanding row in the unit-wide payments rollup. */
export interface PaymentOutstanding {
  compensationId: string;
  splitId: string;
  projectId: string;
  label: string;
  user: string;
  amount: number;
  state: SplitState;
}

/** Per-person totals in the payments rollup. */
export interface PaymentByPerson {
  user: string;
  outstanding: number;
  received: number;
}

export interface PaymentsRollup {
  outstanding: PaymentOutstanding[];
  byPerson: PaymentByPerson[];
}

/** Body for replacing a compensation's split allocation. */
export interface AllocateSplitsBody {
  base?: number;
  note?: string;
  /** LEGACY ungrouped %-driven: % of remainder (number → derived, null → manual). */
  allocPct?: number | null;
  /** Group membership; null → ungroup. */
  groupId?: string | null;
  /** Within-group % (grouped %-driven); null → pinned. */
  groupTaskPct?: number | null;
  splits: Array<{ user: string; pct: number; overrideAmount?: number; overrideReason?: string; received?: boolean; receivedOn?: string }>;
}

const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

/** Thrown for any non-2xx response. `status` lets callers special-case 401/403/429/400. */
export class ApiClientError extends Error {
  readonly status: number;
  readonly body: ApiError | null;
  constructor(status: number, body: ApiError | null, message: string) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.body = body;
  }
  get isAuth(): boolean { return this.status === 401; }
  get isForbidden(): boolean { return this.status === 403; }
  get isRateLimited(): boolean { return this.status === 429; }
  get isValidation(): boolean { return this.status === 400; }
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/* ------------------------------------------------------------------ *
 * CSRF token cache (in-memory; the secret cookie is httpOnly).
 * ------------------------------------------------------------------ */

interface CsrfResponse { csrfToken: string }

let csrfTokenCache: string | null = null;
let csrfInflight: Promise<string> | null = null;

/** Hit `GET /api/csrf`, cache the returned token in memory, and return it. */
async function refreshCsrf(): Promise<string> {
  if (csrfInflight) return csrfInflight;
  csrfInflight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/csrf`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) throw new ApiClientError(res.status, null, `CSRF token fetch failed (${res.status})`);
      const data = (await res.json()) as CsrfResponse;
      csrfTokenCache = data.csrfToken;
      return csrfTokenCache;
    } finally {
      csrfInflight = null;
    }
  })();
  return csrfInflight;
}

/** Return the cached CSRF token, fetching one if we don't have it yet. */
async function ensureCsrf(): Promise<string> {
  if (csrfTokenCache) return csrfTokenCache;
  return refreshCsrf();
}

interface RequestOptions {
  method?: string;
  /** JSON body (sent as application/json). Mutually exclusive with `form`. */
  body?: unknown;
  /** Pre-built FormData for multipart requests; the browser sets the boundary. */
  form?: FormData;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Response handling: 'json' (default) or 'blob' for binary downloads (e.g. PDF). */
  responseType?: 'json' | 'blob';
  /** Internal: set on the single CSRF-refresh retry to avoid infinite loops. */
  _retried?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = { Accept: 'application/json' };

  let url = `${API_BASE}${path}`;
  if (opts.query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined) qs.append(k, String(v));
    }
    const s = qs.toString();
    if (s) url += `?${s}`;
  }

  const init: RequestInit = { method, headers, credentials: 'include', signal: opts.signal };

  if (opts.form !== undefined) {
    // Multipart: do NOT set Content-Type — the browser adds the boundary.
    init.body = opts.form;
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }

  if (MUTATING.has(method)) {
    const token = await ensureCsrf();
    if (token) headers['X-CSRF-Token'] = token;
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    throw new ApiClientError(0, null, e instanceof Error ? e.message : 'Network error');
  }

  if (res.status === 204) return undefined as T;

  if (opts.responseType === 'blob') {
    if (!res.ok) {
      const t = await res.text(); let b: any = null; try { b = JSON.parse(t); } catch { /* not json */ }
      if (res.status === 403 && MUTATING.has(method) && !opts._retried) { await refreshCsrf(); return request<T>(path, { ...opts, _retried: true }); }
      throw new ApiClientError(res.status, b, b?.message ?? `HTTP ${res.status}`);
    }
    return (await res.blob()) as unknown as T;
  }

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }

  if (!res.ok) {
    const body = (parsed && typeof parsed === 'object') ? (parsed as ApiError) : null;
    const message = body?.message ?? `HTTP ${res.status}`;
    // A mutating request rejected for a stale/missing CSRF token: refresh once
    // and retry. We only retry when we still have an auth session (403, not 401).
    if (res.status === 403 && MUTATING.has(method) && !opts._retried) {
      await refreshCsrf();
      return request<T>(path, { ...opts, _retried: true });
    }
    throw new ApiClientError(res.status, body, message);
  }
  return parsed as T;
}

/* ------------------------------------------------------------------ *
 * Resource-grouped client. Wave 3 fills the server handlers; the web
 * client only depends on these signatures.
 * ------------------------------------------------------------------ */
/** Fields the multipart registration form collects (files attached separately). */
export interface RegisterFields {
  name: string;
  email: string;
  password: string;
  affiliation: string;
  position: string;
  /** Requested role label. The server stores it; the admin assigns the real role on approval. */
  role: string;
  phone?: string;
  expertise?: string;
}

/** The three compliance documents uploaded at registration. */
export interface RegisterFiles {
  cv?: File | null;
  gcp?: File | null;
  hsp?: File | null;
}

/** Generic confirmation returned by POST /api/auth/register. */
export interface RegisterResponse {
  status: 'pending';
  message: string;
}

export const apiClient = {
  health: () => request<HealthResponse>('/health'),

  /** Fetch + cache the CSRF token. Call on bootstrap and after login/logout. */
  refreshCsrf,

  auth: {
    me: () => request<MeResponse>('/auth/me'),
    login: (body: LoginRequest) => request<MeResponse>('/auth/login', { method: 'POST', body }),
    logout: () => request<void>('/auth/logout', { method: 'POST' }),
    /**
     * Self-registration — multipart/form-data (text fields + cv/gcp/hsp files).
     * Returns the generic pending-approval confirmation.
     */
    register: (fields: RegisterFields, files: RegisterFiles = {}) => {
      const form = new FormData();
      form.append('name', fields.name);
      form.append('email', fields.email);
      form.append('password', fields.password);
      form.append('affiliation', fields.affiliation);
      form.append('position', fields.position);
      form.append('role', fields.role);
      if (fields.phone) form.append('phone', fields.phone);
      if (fields.expertise) form.append('expertise', fields.expertise);
      if (files.cv) form.append('cv', files.cv);
      if (files.gcp) form.append('gcp', files.gcp);
      if (files.hsp) form.append('hsp', files.hsp);
      return request<RegisterResponse>('/auth/register', { method: 'POST', form });
    },
    /** "Forgot password" — raises an admin-triaged reset request. Always generic. */
    forgotPassword: (email: string) =>
      request<ForgotPasswordResponse>('/auth/forgot-password', { method: 'POST', body: { email } }),
  },

  projects: {
    list: () => request<Project[]>('/projects'),
    get: (id: string) => request<Project>(`/projects/${id}`),
    create: (body: Partial<Project>) => request<Project>('/projects', { method: 'POST', body }),
    update: (id: string, body: Partial<Project>) => request<Project>(`/projects/${id}`, { method: 'PATCH', body }),
    /** SOFT delete — the project moves to the recycle bin (restorable 30 days). */
    remove: (id: string) => request<{ ok: boolean; recoverUntil: string }>(`/projects/${id}`, { method: 'DELETE' }),
    /** Recycle bin: list soft-deleted projects (delete-capable roles only). */
    listDeleted: () => request<Project[]>('/projects/deleted'),
    /** Restore a soft-deleted project from the recycle bin. */
    restore: (id: string) => request<Project>(`/projects/${id}/restore`, { method: 'POST' }),
    addMember: (id: string, userId: string) =>
      request<Project>(`/projects/${id}/members`, { method: 'POST', body: { userId } }),
    removeMember: (id: string, userId: string) =>
      request<Project>(`/projects/${id}/members/${userId}`, { method: 'DELETE' }),
    /** Advisory edit presence: heartbeat my builder session (returns the OTHER
     *  current editors) + release my entry. */
    editing: (id: string) =>
      request<{ others: Array<{ id: string; name: string; lastActiveAt: string }> }>(
        `/projects/${id}/editing`, { method: 'POST' }),
    editingRelease: (id: string) =>
      request<{ ok: boolean }>(`/projects/${id}/editing`, { method: 'DELETE' }),
    /** Project-scoped reads (read-only this milestone). */
    timeline: (id: string) => request<TimelineItem[]>(`/projects/${id}/timeline`),
    activity: (id: string) => request<ActivityLog[]>(`/projects/${id}/activity`),
    documents: (id: string) => request<DocumentMeta[]>(`/projects/${id}/documents`),
    meetings: (id: string) => request<Meeting[]>(`/projects/${id}/meetings`),
  },

  timeline: {
    list: (projectId?: string) => request<TimelineItem[]>('/timeline', { query: { project: projectId } }),
    create: (body: Partial<TimelineItem> & { project: string }) =>
      request<TimelineItem>('/timeline', { method: 'POST', body }),
    update: (taskId: string, body: Partial<TimelineItem>) =>
      request<TimelineItem>(`/timeline/${taskId}`, { method: 'PATCH', body }),
    setStatus: (taskId: string, status: TaskStatus) =>
      request<TimelineItem>(`/timeline/${taskId}/status`, { method: 'PATCH', body: { status } }),
    remove: (taskId: string) =>
      request<{ ok: boolean; id: string }>(`/timeline/${taskId}`, { method: 'DELETE' }),
  },

  quotations: {
    list: (projectId?: string) => request<Quotation[]>('/quotations', { query: { project: projectId } }),
    get: (id: string) => request<Quotation>(`/quotations/${id}`),
    /** Stateless preview from a builder form (no row created). */
    compute: (form: BuilderForm) =>
      request<QuotationComputeResult>('/quotations/compute', { method: 'POST', body: form }),
    /** Create a Draft version. Body accepts `sections` OR a builder `form`. */
    create: (body: {
      projectId: string;
      sections?: QuotationSection[];
      form?: BuilderForm;
      number?: string;
      version?: string;
      submitted?: string;
      adjustments?: Partial<QuotationAdjustments>;
    }) => request<Quotation>('/quotations', { method: 'POST', body }),
    update: (id: string, body: { sections?: QuotationSection[]; form?: BuilderForm; number?: string; version?: string; submitted?: string }) =>
      request<Quotation>(`/quotations/${id}`, { method: 'PATCH', body }),
    adjustments: (id: string, adj: Partial<QuotationAdjustments>) =>
      request<Quotation>(`/quotations/${id}/adjustments`, { method: 'PATCH', body: adj }),
    lifecycle: (id: string, status: QuotationStatus) =>
      request<Quotation>(`/quotations/${id}/lifecycle`, { method: 'POST', body: { status } }),
    /** Professional quotation PDF (compiled server-side by pdflatex). Returns a PDF Blob. */
    pdf: (body: {
      studyCode: string;
      studyTitle: string;
      quotationNumber: string;
      version: string;
      dateText: string;
      client?: string;
      currency?: string;
      grandTotal: number;
      subtotal?: number;
      sections: { title: string; total: number; items: { code?: string; name: string; qty: number; unit?: string; price: number; total: number }[] }[];
      adjustments?: { label: string; amount: number }[];
    }) => request<Blob>('/quotations/pdf', { method: 'POST', body, responseType: 'blob' }),
  },

  pricing: {
    get: () => request<PricingLibrary>('/pricing'),
    protocol: {
      create: (body: Omit<PricingProtocolRow, 'id'>) =>
        request<PricingProtocolRow>('/pricing/protocol', { method: 'POST', body }),
      update: (id: string, body: Partial<Omit<PricingProtocolRow, 'id'>>) =>
        request<PricingProtocolRow>(`/pricing/protocol/${id}`, { method: 'PUT', body }),
      remove: (id: string) => request<{ ok: boolean }>(`/pricing/protocol/${id}`, { method: 'DELETE' }),
    },
    descriptive: {
      create: (body: Omit<PricingDescriptiveRow, 'id'>) =>
        request<PricingDescriptiveRow>('/pricing/descriptive', { method: 'POST', body }),
      update: (id: string, body: Partial<Omit<PricingDescriptiveRow, 'id'>>) =>
        request<PricingDescriptiveRow>(`/pricing/descriptive/${id}`, { method: 'PUT', body }),
      remove: (id: string) => request<{ ok: boolean }>(`/pricing/descriptive/${id}`, { method: 'DELETE' }),
    },
    topup: {
      create: (body: Omit<PricingTopupRow, 'id'>) =>
        request<PricingTopupRow>('/pricing/topup', { method: 'POST', body }),
      update: (id: string, body: Partial<Omit<PricingTopupRow, 'id'>>) =>
        request<PricingTopupRow>(`/pricing/topup/${id}`, { method: 'PUT', body }),
      remove: (id: string) => request<{ ok: boolean }>(`/pricing/topup/${id}`, { method: 'DELETE' }),
    },
  },

  /** Lab/procedure/variable catalog (the shared library + admin promotion queue). */
  catalog: {
    list: (query?: { kind?: string; status?: string; scope?: string }) =>
      request<CatalogItem[]>('/catalog', { query }),
    propose: (body: { kind: string; name: string; groupName?: string; price?: number; unit?: string; parameters?: { name: string; analyzed: boolean }[]; proposeToLibrary?: boolean; projectId?: string }) =>
      request<CatalogItem>('/catalog/propose', { method: 'POST', body }),
    approve: (id: string, body?: { name?: string; code?: string; groupName?: string; price?: number | null; unit?: string; parameters?: { name: string; analyzed: boolean }[] }) =>
      request<CatalogItem>(`/catalog/${id}/approve`, { method: 'PATCH', body: body ?? {} }),
    reject: (id: string, reviewNote?: string) =>
      request<CatalogItem>(`/catalog/${id}/reject`, { method: 'PATCH', body: { reviewNote } }),
    update: (id: string, body: { name?: string; code?: string; groupName?: string; price?: number | null; unit?: string; parameters?: { name: string; analyzed: boolean }[] }) =>
      request<CatalogItem>(`/catalog/${id}`, { method: 'PATCH', body }),
    /** Admin direct-create: lands an ACTIVE library item in one step. */
    create: (body: { kind: string; name: string; code?: string; groupName?: string; price?: number | null; unit?: string; parameters?: { name: string; analyzed: boolean }[] }) =>
      request<CatalogItem>('/catalog', { method: 'POST', body }),
    remove: (id: string) => request<{ ok: boolean }>(`/catalog/${id}`, { method: 'DELETE' }),
  },

  /** Data-Management cost catalog (DMP/CRF databank — admin-editable; the
   *  quotation engine prices DMP/CRF from these rows, constant as fallback). */
  dmCatalog: {
    list: () => request<DataManagementItem[]>('/dm-catalog'),
    create: (body: Partial<DataManagementItem>) =>
      request<DataManagementItem>('/dm-catalog', { method: 'POST', body }),
    update: (id: string, body: Partial<DataManagementItem>) =>
      request<DataManagementItem>(`/dm-catalog/${id}`, { method: 'PATCH', body }),
    remove: (id: string) => request<{ ok: boolean }>(`/dm-catalog/${id}`, { method: 'DELETE' }),
    /** One-click seed of the table from the built-in defaults (idempotent). */
    seed: () => request<{ ok: boolean; seeded: number }>('/dm-catalog/seed', { method: 'POST' }),
  },

  /** CONSORT 2025 flow-diagram PDF (compiled server-side by pdflatex). Returns a PDF Blob. */
  consort: {
    pdf: (body: { title: string; subtitle: string; isCohort: boolean; screened: number; excluded: number; enrolled: number; enrolLabel: string; primary?: string; arms: { label: string; n: number }[]; boxLabels?: { screened?: string; excluded?: string; analysed?: string; enrolled?: string } }) =>
      request<Blob>('/consort/pdf', { method: 'POST', body, responseType: 'blob' }),
  },

  /** Monthly team-workload dashboard PDF (People × Months matrix, compiled server-side by pdflatex). Returns a PDF Blob. */
  dashboard: {
    pdf: (body: {
      title: string; subtitle?: string; dateText: string; months: string[];
      rows: { name: string; role?: string; counts: number[] }[];
      summary?: { activeTaskMonths?: number; heaviestName?: string; heaviestTotal?: number; overloadedCount?: number; freeCount?: number; periodLabel?: string };
    }) => request<Blob>('/dashboard/pdf', { method: 'POST', body, responseType: 'blob' }),
  },

  compensation: {
    list: (projectId?: string) =>
      request<CompensationView[]>('/compensation', { query: { project: projectId } }),
    get: (id: string) => request<CompensationView>(`/compensation/${id}`),
    /** Replace a compensation's split allocation (server enforces Σpct === 100). */
    allocateSplits: (id: string, body: AllocateSplitsBody) =>
      request<CompensationView>(`/compensation/${id}/splits`, { method: 'POST', body }),
    /** Tick (or clear) a single split as received. `on` defaults to true. */
    setReceived: (splitId: string, on = true, receivedOn?: string) =>
      request<{ splitId: string; user: string; pct: number; received: boolean; receivedOn: string }>(
        `/compensation/splits/${splitId}/received`,
        { method: 'PATCH', body: { received: on, ...(receivedOn ? { receivedOn } : {}) } },
      ),
    /** Create a compensation row for a task (splits set separately via allocateSplits). */
    create: (body: { taskId: string; label?: string; base?: number; allocPct?: number | null; groupId?: string | null; groupTaskPct?: number | null }) =>
      request<CompensationView>('/compensation', { method: 'POST', body }),
    /** Persist a new manual row order within a project (drag-to-reorder). */
    reorder: (ids: string[]) =>
      request<{ ok: boolean }>('/compensation/reorder', { method: 'POST', body: { ids } }),
    /** Move a task to a group / set its within-group % (clamped server-side to ≤100). */
    move: (id: string, body: { groupId?: string | null; groupTaskPct?: number | null }) =>
      request<CompensationView>(`/compensation/${id}/group`, { method: 'PATCH', body }),
    /** Delete a compensation row (+ its splits). */
    remove: (id: string) => request<{ ok: boolean }>(`/compensation/${id}`, { method: 'DELETE' }),
    /** Unit-wide outstanding + by-person rollup. */
    payments: () => request<PaymentsRollup>('/compensation/payments'),
    /** Personal earnings statement PDF (compiled server-side; mirrors the quotation
     *  PDF blob pattern). `year` filters to a calendar year or 'all'. Returns a PDF Blob. */
    statementPdf: (body: { year?: number | 'all' }) =>
      request<Blob>('/compensation/statement/pdf', { method: 'POST', body, responseType: 'blob' }),
    /** Nudge users with outstanding payments (admin/finance). Omit `userIds` for all. */
    remind: (userIds?: string[]) =>
      request<{ ok: boolean; reminded: number; users: string[] }>('/compensation/remind', { method: 'POST', body: { userIds } }),
    /** Compensation GROUPS (3-level money model). */
    groups: {
      list: (projectId?: string) =>
        request<CompensationGroup[]>('/compensation/groups', { query: { project: projectId } }),
      create: (body: { projectId: string; name: string; groupPct?: number }) =>
        request<CompensationGroup>('/compensation/groups', { method: 'POST', body }),
      update: (id: string, body: { name?: string; groupPct?: number }) =>
        request<CompensationGroup>(`/compensation/groups/${id}`, { method: 'PATCH', body }),
      remove: (id: string) =>
        request<{ ok: boolean }>(`/compensation/groups/${id}`, { method: 'DELETE' }),
      reorder: (ids: string[]) =>
        request<{ ok: boolean }>('/compensation/groups/reorder', { method: 'POST', body: { ids } }),
      /** Ensure default groups + bucket tasks + even-distribute (new/no-money projects). */
      seedDefaults: (projectId: string) =>
        request<{ ok: boolean; groups: CompensationGroup[] }>('/compensation/groups/seed-defaults', { method: 'POST', body: { projectId } }),
    },
  },

  users: {
    /** Roster of real users, optionally filtered by status (default: server's default set). */
    list: (status?: 'Active' | 'Suspended' | 'all') =>
      request<User[]>('/users', { query: { status } }),
    pending: () => request<PendingUser[]>('/users/pending'),
    /** Promote a pending applicant. `roleKey` is the canonical RBAC role to assign. */
    approve: (id: string, body: { roleKey: Role; role?: string; team?: string }) =>
      request<User>(`/users/pending/${id}/approve`, { method: 'POST', body }),
    reject: (id: string, reason?: string) =>
      request<{ ok: boolean; id: string }>(`/users/pending/${id}/reject`, {
        method: 'POST',
        body: reason ? { reason } : {},
      }),
    /** Change an existing user's role (super/admin only; server enforces super-only guards). */
    setRole: (id: string, body: { roleKey: Role; role?: string }) =>
      request<User>(`/users/${id}/role`, { method: 'PATCH', body }),
    /** Suspend / reactivate a user (admin). */
    setStatus: (id: string, status: 'Active' | 'Suspended') =>
      request<User>(`/users/${id}/status`, { method: 'PATCH', body: { status } }),
    /** Issue a temporary password for a user (admin). Optionally resolves the
     *  originating password-reset request row. Returns the plaintext temp password once. */
    resetPassword: (id: string, requestId?: string) =>
      request<ResetPasswordResponse>(`/users/${id}/reset-password`, { method: 'POST', body: { requestId } }),
    /** Password-reset request admin queue. */
    passwordResets: {
      list: (status?: PasswordResetRequestStatus | 'all') =>
        request<PasswordResetRequest[]>('/users/password-resets', { query: { status } }),
      dismiss: (id: string) =>
        request<{ ok: boolean }>(`/users/password-resets/${id}/dismiss`, { method: 'POST' }),
    },

    /** Minimal roster ANY authenticated user may read (powers clickable profile names). */
    roster: () => request<PublicUserSummary[]>('/users/roster'),
    /** ANY user's viewable profile (CV history + certificates + signature). */
    profile: (id: string) => request<PublicProfileResponse>(`/users/${id}/profile`),
    /** Authenticated file URLs for another user's CV / certificate (cookie-auth <a>/preview). */
    cvFileUrl: (userId: string, cvId: string) => `${API_BASE}/users/${userId}/cv/${cvId}/file`,
    certFileUrl: (userId: string, certId: string) => `${API_BASE}/users/${userId}/certificates/${certId}/file`,

    /** Self-service profile (My Profile page) — always the authenticated user. */
    me: {
      profile: () => request<MyProfileResponse>('/users/me/profile'),
      saveProfile: (body: UserProfile) =>
        request<{ profile: UserProfile }>('/users/me/profile', { method: 'PUT', body }),
      /** Upload + stamp a CV PDF; returns the new current version with its real SHA-256. */
      uploadCv: (file: File) => {
        const form = new FormData();
        form.append('file', file);
        return request<UserCvEntry>('/users/me/cv', { method: 'POST', form });
      },
      cvFileUrl: (id: string) => `${API_BASE}/users/me/cv/${id}/file`,
      addCertificate: (input: { name: string; expiry: string; file?: File }) => {
        const form = new FormData();
        form.append('name', input.name);
        form.append('expiry', input.expiry);
        if (input.file) form.append('file', input.file);
        return request<UserCertificateEntry>('/users/me/certificates', { method: 'POST', form });
      },
      updateCertificate: (id: string, input: { name?: string; expiry?: string; file?: File }) => {
        const form = new FormData();
        if (input.name !== undefined) form.append('name', input.name);
        if (input.expiry !== undefined) form.append('expiry', input.expiry);
        if (input.file) form.append('file', input.file);
        return request<UserCertificateEntry>(`/users/me/certificates/${id}`, { method: 'PUT', form });
      },
      deleteCertificate: (id: string) =>
        request<void>(`/users/me/certificates/${id}`, { method: 'DELETE' }),
      certFileUrl: (id: string) => `${API_BASE}/users/me/certificates/${id}/file`,
      /** Self-service password change (verifies the current password server-side). */
      changePassword: (body: ChangePasswordRequest) =>
        request<{ ok: boolean }>('/users/me/password', { method: 'PUT', body }),
    },
  },

  activity: {
    /** Activity feed for one project, newest-first. */
    list: (projectId: string) => request<ActivityLog[]>(`/projects/${projectId}/activity`),
  },

  /** Documents — list (read), upload new version, status workflow, file download. */
  documents: {
    list: (projectId?: string) => request<DocumentMeta[]>('/documents', { query: { project: projectId } }),
    /** Upload a new document/version (multipart). Archives prior versions of the same type.
     *  `version` is an optional manual label (blank → server auto-increments v{n}.0). */
    upload: (input: { projectId: string; type: string; status?: DocumentStatus; category?: string; description?: string; version?: string; file: File }) => {
      const form = new FormData();
      form.append('projectId', input.projectId);
      form.append('type', input.type);
      if (input.status) form.append('status', input.status);
      if (input.category) form.append('category', input.category);
      if (input.description) form.append('description', input.description);
      if (input.version) form.append('version', input.version);
      form.append('file', input.file);
      return request<DocumentMeta>('/documents', { method: 'POST', form });
    },
    setStatus: (id: string, status: DocumentStatus) =>
      request<DocumentMeta>(`/documents/${id}/status`, { method: 'PATCH', body: { status } }),
    /** Delete a document version; promotes the newest remaining version if it was current. */
    remove: (id: string) =>
      request<{ ok: boolean; promoted: DocumentMeta | null }>(`/documents/${id}`, { method: 'DELETE' }),
    /** Same-origin URL for the stored file (cookie auth rides along on <a>/preview). */
    fileUrl: (id: string) => `${API_BASE}/documents/${id}/file`,
  },
  meetings: {
    list: (projectId?: string) => request<Meeting[]>('/meetings', { query: { project: projectId } }),
    create: (body: MeetingWriteBody & { projectId: string; title: string; date: string }) =>
      request<MeetingWriteResult>('/meetings', { method: 'POST', body }),
    update: (id: string, body: MeetingWriteBody) =>
      request<MeetingWriteResult>(`/meetings/${id}`, { method: 'PATCH', body }),
  },

  /** In-app notifications (bell menu). */
  notifications: {
    list: (q?: { limit?: number; offset?: number }) =>
      request<AppNotification[]>('/notifications', { query: q }),
    unreadCount: () => request<{ count: number }>('/notifications/unread-count'),
    markRead: (id: string) =>
      request<{ ok: boolean }>(`/notifications/${id}/read`, { method: 'PATCH' }),
    markAllRead: () =>
      request<{ ok: boolean }>('/notifications/read-all', { method: 'POST' }),
  },

  /** Reusable Project-Builder templates (admin). */
  templates: {
    list: () => request<ProjectTemplate[]>('/templates'),
    create: (body: { name: string; description?: string; builder: Record<string, unknown> }) =>
      request<ProjectTemplate>('/templates', { method: 'POST', body }),
    remove: (id: string) => request<{ ok: boolean }>(`/templates/${id}`, { method: 'DELETE' }),
  },

  /** Admin-tuned analysis-cost formula (singleton). */
  analysisFormula: {
    get: () => request<AnalysisFormula>('/analysis-formula'),
    update: (body: Partial<AnalysisFormula>) =>
      request<AnalysisFormula>('/analysis-formula', { method: 'PUT', body }),
  },
};

/** Scheduling + log fields a meeting create/update accepts (senior request). */
export interface MeetingWriteBody {
  projectId?: string;
  title?: string;
  date?: string;
  attendees?: string[];
  decisions?: number;
  actions?: number;
  notes?: string;
  decisionsText?: string;
  actionItems?: string[];
  followUps?: string[];
  startTime?: string;
  endTime?: string;
  location?: string;
  meetingLink?: string;
  status?: 'scheduled' | 'completed' | 'cancelled';
  /** Email an invite/update/cancellation to attendees. */
  sendEmail?: boolean;
}

/** A meeting write returns the DTO plus the email outcome (when sendEmail was set). */
export type MeetingWriteResult = Meeting & {
  mailResult?: { disabled: boolean; sent: number; failed: number };
};

export type ApiClient = typeof apiClient;
